import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { AppointmentDetail } from '../../domain/models/appointment-detail.model';
import { ClientRef } from '../../domain/models/client-ref.model';
import { CreateTaskInput } from '../../domain/models/create-task-input.model';
import { DashboardSummary } from '../../domain/models/dashboard-summary.model';
import { PendingTask } from '../../domain/models/pending-task.model';
import { Professional } from '../../domain/models/professional.model';
import { ReasonType } from '../../domain/models/reason-type.model';
import { TasksPage } from '../../domain/models/tasks-page.model';
import { IDashboardRepository } from '../../domain/repositories/dashboard.repository';
import { DashboardRemoteDatasource } from '../datasources/remote/dashboard.remote-datasource';
import { DashboardMapper } from '../mappers/dashboard.mapper';

@Injectable()
export class DashboardRepositoryImpl implements IDashboardRepository {
  private readonly datasource = inject(DashboardRemoteDatasource);
  private readonly mapper = inject(DashboardMapper);

  public getSummary(departmentId?: number): Observable<DashboardSummary> {
    return this.datasource.getSummary(departmentId).pipe(map((dto) => this.mapper.toDashboardSummary(dto)));
  }

  public createTask(input: CreateTaskInput): Observable<PendingTask> {
    const payload = this.mapper.toCreateTaskPayload(input);
    return this.datasource.createTask(payload).pipe(map((dto) => this.mapper.toPendingTask(dto)));
  }

  public updateTask(id: string, input: CreateTaskInput): Observable<PendingTask> {
    const payload = this.mapper.toCreateTaskPayload(input);
    return this.datasource.updateTask(id, payload).pipe(map((dto) => this.mapper.toPendingTask(dto)));
  }

  public toggleTask(id: string, completed: boolean): Observable<PendingTask> {
    const payload = this.mapper.toToggleTaskPayload(completed);
    return this.datasource.toggleTask(id, payload).pipe(map((dto) => this.mapper.toPendingTask(dto)));
  }

  public deleteTask(id: string): Observable<void> {
    return this.datasource.deleteTask(id);
  }

  public getTasks(status: 1 | 2, page?: number, pageSize?: number): Observable<TasksPage> {
    return this.datasource.getTasks(status, page, pageSize).pipe(
      map((dto) => ({
        tasks: dto.Items.map((item) => this.mapper.toPendingTask(item)),
        totalCount: dto.TotalCount,
      })),
    );
  }

  public listReasonTypes(): Observable<readonly ReasonType[]> {
    return this.datasource.listReasonTypes().pipe(
      map((dto) => dto.Items.map((item) => this.mapper.toReasonType(item))),
    );
  }

  public getAppointmentDetail(id: string): Observable<AppointmentDetail> {
    return this.datasource.getAppointmentById(id).pipe(map((dto) => this.mapper.toAppointmentDetail(dto)));
  }

  public cancelAppointment(id: string): Observable<void> {
    return this.datasource.cancelAppointment(id);
  }

  public listProfessionals(): Observable<readonly Professional[]> {
    return this.datasource.listProfessionals().pipe(
      map((dto) => dto.Items.map((item) => this.mapper.toProfessional(item))),
    );
  }

  public getClientById(id: string): Observable<ClientRef | null> {
    return this.datasource.getClientById(id).pipe(
      map((dto) => this.mapper.toClientRef(dto)),
      catchError(() => of(null)),
    );
  }

  public searchClients(term: string): Observable<readonly ClientRef[]> {
    return this.datasource.searchClients(term).pipe(
      map((dto) => dto.Items.map((item) => this.mapper.toClientRef(item))),
    );
  }
}
